
import { useEffect, useRef, useState } from 'react';

export default function Values() {
  const [isVisible, setIsVisible] = useState(false);
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: 'ri-focus-3-line',
      title: 'Precision First',
      description:
        'Every measurement, every cut, every finish is executed with millimeter-perfect accuracy. We do not approximate—we deliver exactness.',
      stat: '0.1mm',
      statLabel: 'Tolerance',
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Uncompromising Quality',
      description:
        'We source only premium materials and employ master craftsmen. Quality is not a variable in our equation—it is the constant.',
      stat: '100%',
      statLabel: 'Premium Materials',
    },
    {
      icon: 'ri-time-line',
      title: 'Deadline Integrity',
      description:
        'When we commit to a timeline, we honor it. Our project management systems ensure on-time delivery without sacrificing quality.',
      stat: '98%',
      statLabel: 'On-Time Delivery',
    },
    {
      icon: 'ri-user-heart-line',
      title: 'Client Partnership',
      description:
        'We view every project as a collaboration. Your vision guides us, our expertise executes it, and together we create something exceptional.',
      stat: '500+',
      statLabel: 'Happy Clients',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Continuous Innovation',
      description:
        'We stay ahead of industry trends, adopting new techniques and technologies that enhance quality and efficiency without compromising craftsmanship.',
      stat: '15+',
      statLabel: 'Years Innovating',
    },
    {
      icon: 'ri-leaf-line',
      title: 'Sustainable Practice',
      description:
        'Excellence includes responsibility. We prioritize sustainable materials and methods that minimize environmental impact while maximizing longevity.',
      stat: '70%',
      statLabel: 'Eco Materials',
    },
  ];

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 lg:py-32 bg-[#0A0A0A] overflow-hidden"
    >
      {/* Animated Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #C9A86C 1px, transparent 0)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>

      {/* Floating Decorative Elements */}
      <div className="absolute top-20 left-10 w-32 h-32 border border-[#C9A86C]/20 rotate-45 hidden lg:block" />
      <div className="absolute bottom-20 right-10 w-24 h-24 border border-[#C9A86C]/20 rotate-12 hidden lg:block" />
      <div className="absolute top-1/2 left-1/4 w-2 h-2 bg-[#C9A86C]/40 rounded-full animate-pulse hidden lg:block" />
      <div
        className="absolute top-1/3 right-1/3 w-3 h-3 bg-[#C9A86C]/30 rounded-full animate-pulse hidden lg:block"
        style={{ animationDelay: '1s' }}
      />

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-8 lg:px-16 relative z-10">
        {/* Header */}
        <div
          className={`text-center mb-12 md:mb-16 lg:mb-20 transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="flex items-center justify-center gap-3 md:gap-4 mb-4 md:mb-6">
            <div className="w-8 md:w-16 h-px bg-gradient-to-r from-transparent to-[#C9A86C]" />
            <span className="text-[#C9A86C] text-xs md:text-sm tracking-[0.2em] md:tracking-[0.3em] uppercase font-light">
              Our Values
            </span>
            <div className="w-8 md:w-16 h-px bg-gradient-to-l from-transparent to-[#C9A86C]" />
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-serif text-white mb-4 md:mb-6 leading-tight">
            What We <span className="text-[#C9A86C]">Stand For</span>
          </h2>
          <p className="text-base md:text-lg text-[#8A8A8A] max-w-3xl mx-auto font-light">
            These principles guide every decision we make and every project we undertake
          </p>
        </div>

        {/* Values Grid - Bento Style */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {values.map((value, index) => (
            <div
              key={index}
              className={`group relative cursor-pointer transition-all duration-700 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              } ${activeCard === index ? 'lg:col-span-2 lg:row-span-1' : ''}`}
              style={{ transitionDelay: `${index * 150}ms` }}
              onClick={() => setActiveCard(activeCard === index ? null : index)}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              {/* Card */}
              <div
                className={`relative h-full min-h-[280px] md:min-h-[320px] p-6 md:p-8 border transition-all duration-500 overflow-hidden ${
                  activeCard === index
                    ? 'bg-[#C9A86C] border-[#C9A86C]'
                    : hoveredCard === index
                    ? 'bg-[#141414] border-[#C9A86C]/60'
                    : 'bg-[#0F0F0F] border-[#1A1A1A]'
                }`}
              >
                {/* Animated Corner Accents */}
                <div
                  className={`absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 transition-all duration-500 ${
                    activeCard === index ? 'border-white/50' : 'border-[#C9A86C]/0 group-hover:border-[#C9A86C]/50'
                  }`}
                />
                <div
                  className={`absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 transition-all duration-500 ${
                    activeCard === index ? 'border-white/50' : 'border-[#C9A86C]/0 group-hover:border-[#C9A86C]/50'
                  }`}
                />

                {/* Glow Effect on Hover */}
                <div
                  className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none ${
                    activeCard === index ? 'hidden' : ''
                  }`}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-[#C9A86C]/5 via-transparent to-transparent" />
                </div>

                {/* Icon with Animation */}
                <div
                  className={`relative w-14 h-14 md:w-16 md:h-16 flex items-center justify-center mb-6 transition-all duration-500 ${
                    activeCard === index ? 'bg-white/20' : 'bg-[#1A1A1A] group-hover:bg-[#C9A86C]/20'
                  }`}
                >
                  <i
                    className={`${value.icon} text-2xl md:text-3xl transition-all duration-500 ${
                      activeCard === index ? 'text-white scale-110' : 'text-[#C9A86C] group-hover:scale-110'
                    }`}
                  />
                  {/* Rotating Border */}
                  <div
                    className={`absolute inset-0 border transition-all duration-700 ${
                      activeCard === index
                        ? 'border-white/30 rotate-45'
                        : 'border-[#C9A86C]/0 group-hover:border-[#C9A86C]/30 group-hover:rotate-45'
                    }`}
                  />
                </div>

                {/* Content */}
                <div className="relative z-10">
                  <h3
                    className={`font-serif text-xl md:text-2xl mb-3 md:mb-4 transition-colors duration-500 ${
                      activeCard === index ? 'text-white' : 'text-white'
                    }`}
                  >
                    {value.title}
                  </h3>

                  <p
                    className={`text-sm md:text-base leading-relaxed transition-all duration-500 ${
                      activeCard === index
                        ? 'text-white/90'
                        : 'text-[#6B6B6B] group-hover:text-[#8A8A8A]'
                    }`}
                  >
                    {value.description}
                  </p>

                  {/* Stat Badge - Appears on Hover/Active */}
                  <div
                    className={`mt-6 pt-6 border-t transition-all duration-500 ${
                      activeCard === index
                        ? 'border-white/20 opacity-100 translate-y-0'
                        : 'border-[#1A1A1A] opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0'
                    }`}
                  >
                    <div className="flex items-end gap-2">
                      <span
                        className={`text-3xl md:text-4xl font-serif transition-colors duration-500 ${
                          activeCard === index ? 'text-white' : 'text-[#C9A86C]'
                        }`}
                      >
                        {value.stat}
                      </span>
                      <span
                        className={`text-xs md:text-sm uppercase tracking-wider mb-1 transition-colors duration-500 ${
                          activeCard === index ? 'text-white/70' : 'text-[#6B6B6B]'
                        }`}
                      >
                        {value.statLabel}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Expand Indicator */}
                <div
                  className={`absolute bottom-4 right-4 w-8 h-8 flex items-center justify-center transition-all duration-500 ${
                    activeCard === index ? 'rotate-45' : 'rotate-0'
                  }`}
                >
                  <i
                    className={`ri-add-line text-lg transition-colors duration-500 ${
                      activeCard === index ? 'text-white' : 'text-[#C9A86C]/50 group-hover:text-[#C9A86C]'
                    }`}
                  />
                </div>

                {/* Number Badge */}
                <div
                  className={`absolute top-6 right-6 font-serif text-5xl md:text-6xl transition-all duration-500 ${
                    activeCard === index ? 'text-white/10' : 'text-[#1A1A1A] group-hover:text-[#C9A86C]/10'
                  }`}
                >
                  0{index + 1}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div
          className={`mt-12 md:mt-16 text-center transition-all duration-1000 delay-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <p className="text-[#6B6B6B] text-sm md:text-base mb-4">
            Ready to experience our commitment to excellence?
          </p>
          <a
            href="/#contact"
            className="inline-flex items-center gap-3 px-8 py-4 bg-transparent border border-[#C9A86C] text-[#C9A86C] hover:bg-[#C9A86C] hover:text-[#0A0A0A] transition-all duration-500 group cursor-pointer"
          >
            <span className="text-sm tracking-wider uppercase">Start Your Project</span>
            <i className="ri-arrow-right-line transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </div>
      </div>
    </section>
  );
}
